import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row, Col, Typography, Form, Input, Button, Card, Space,
  message, Result,
} from 'antd';
import {
  MailOutlined, PhoneOutlined, EnvironmentOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { scenarioSiteAPI } from '../../services/api';
import { getContentWrapperStyle } from './templateStyles';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

export default function ScenarioSiteContact({ slug, siteInfo }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const productId = searchParams.get('product');
  const productName = searchParams.get('name');

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : undefined;
  const cardShadow = isMinimal ? 'none' : isClassic ? '0 1px 4px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.1)';
  const contentFont = getContentWrapperStyle(template, brandColor);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await scenarioSiteAPI.submitInquiry(slug, {
        ...values,
        product_id: productId ? parseInt(productId) : undefined,
        inquiry_type: 'inquiry',
      });
      setSubmitted(true);
    } catch (e) {
      message.error(t('publicSite.contact.submitFailed') + '：' + (e.message || ''));
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '60px 24px' }}>
        <Result
          status="success"
          title={t('publicSite.contact.submitSuccess')}
          subTitle={t('publicSite.contact.submitSuccessMsg')}
          extra={
            <Button type="primary" onClick={() => { setSubmitted(false); form.resetFields(); }}>
              {t('publicSite.contact.inquireAgain')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 24px', ...contentFont }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {t('publicSite.contact.title')}
      </Title>
      <Paragraph type="secondary" style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 40,
      }}>
        {t('publicSite.contact.subtitle')}
      </Paragraph>

      <Row gutter={[40, 32]}>
        <Col xs={24} md={14}>
          <Card style={{
            borderRadius: cardRadius,
            border: isMinimal ? '1px solid #e8e8e8' : undefined,
            boxShadow: cardShadow,
          }}>
            {productName && (
              <div style={{
                background: `${brandColor}11`, border: `1px solid ${brandColor}33`,
                borderRadius: isMinimal ? 0 : 6, padding: '8px 12px', marginBottom: 16,
              }}>
                <Text>{t('publicSite.contact.productLabel')}<Text strong>{decodeURIComponent(productName)}</Text></Text>
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                message: productName ? t('publicSite.contact.defaultMessage', { product: decodeURIComponent(productName) }) : '',
              }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="company_name" label={t('publicSite.form.company')}>
                    <Input placeholder={t('publicSite.form.companyPlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="contact_name" label={t('publicSite.form.contact')} rules={[{ required: true, message: t('publicSite.form.nameRequired') }]}>
                    <Input placeholder={t('publicSite.form.contactPlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="email" label={t('publicSite.form.email')} rules={[
                    { required: true, message: t('publicSite.form.emailRequired') },
                    { type: 'email', message: t('publicSite.form.emailInvalid') },
                  ]}>
                    <Input placeholder={t('publicSite.form.emailPlaceholder')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="phone" label={t('publicSite.form.phone')}>
                    <Input placeholder={t('publicSite.form.phonePlaceholder')} />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="message" label={t('publicSite.form.message')} rules={[{ required: true, message: t('publicSite.form.messageRequired') }]}>
                <TextArea rows={5} placeholder={t('publicSite.form.messagePlaceholder')} />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  icon={<SendOutlined />}
                  size="large"
                  block
                  style={{
                    background: brandColor,
                    borderColor: brandColor,
                    borderRadius: btnRadius,
                  }}
                >
                  {t('publicSite.contact.submit')}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card style={{
            background: isMinimal ? '#fff' : '#fafafa',
            borderRadius: cardRadius,
            border: isMinimal ? '1px solid #e8e8e8' : undefined,
            boxShadow: cardShadow,
          }}>
            <Title level={4} style={{ marginTop: 0 }}>{t('publicSite.contact.contactInfo')}</Title>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {siteInfo?.company_name && (
                <div>
                  <Text strong style={{ fontSize: 16 }}>{siteInfo.company_name}</Text>
                </div>
              )}
              {siteInfo?.contact_email && (
                <Space>
                  <MailOutlined style={{ color: brandColor }} />
                  <a href={`mailto:${siteInfo.contact_email}`}>{siteInfo.contact_email}</a>
                </Space>
              )}
              {siteInfo?.contact_phone && (
                <Space>
                  <PhoneOutlined style={{ color: brandColor }} />
                  <a href={`tel:${siteInfo.contact_phone}`}>{siteInfo.contact_phone}</a>
                </Space>
              )}
              {siteInfo?.address && (
                <Space align="start">
                  <EnvironmentOutlined style={{ color: brandColor, marginTop: 4 }} />
                  <Text>{siteInfo.address}</Text>
                </Space>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
